// src/features/account/components/ApiKeysTab.jsx
import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, AlertCircle, Check, Copy, Eye, EyeOff, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';

const API_ENDPOINTS = [
  // Browse & Search
  { method: 'GET', path: 'marketplace?search=query&category=cat', desc: 'Browse/search public workflows' },
  { method: 'GET', path: 'marketplace?id=UUID', desc: 'Get workflow details' },
  // Comments & Likes
  { method: 'GET', path: 'marketplace-comments?listingId=UUID', desc: 'Get comments on a workflow' },
  { method: 'POST', path: 'marketplace-comments', desc: 'Post a comment', body: '{ "listingId": "...", "body": "..." }' },
  { method: 'POST', path: 'marketplace-like', desc: 'Toggle like on a workflow', body: '{ "listingId": "..." }' },
  // Downloads & My Content
  { method: 'GET', path: 'marketplace-download?id=UUID', desc: 'Get download URLs (Pro users get official workflows free)' },
  { method: 'GET', path: 'marketplace-my-downloads', desc: 'List your downloads' },
  { method: 'GET', path: 'marketplace-my-workflows', desc: 'List your workflows (includes private)' },
  // Upload (2-step: init → S3 PUT → complete) — requires verified payment method
  { method: 'POST', path: 'marketplace-upload-init', desc: 'Initialize upload (CC required)', body: '{ "metadata": { "title": "..." }, "files": [...] }' },
  { method: 'POST', path: 'marketplace-upload-complete', desc: 'Complete upload — supports visibility: "public"|"private"', body: '{ "listingId": "...", "title": "...", "visibility": "public", ... }' },
  // Web App Listings — requires verified payment method
  { method: 'POST', path: 'marketplace-webapp-upload', desc: 'Initialize web app listing (CC required)', body: '{ "title": "...", "appUrl": "...", ... }' },
  { method: 'POST', path: 'marketplace-webapp-complete', desc: 'Complete web app listing', body: '{ "listingId": "...", ... }' },
  // Subscription
  { method: 'GET', path: 'get-subscription', desc: 'Get subscription status (includes tier: free|pro)' },
  { method: 'POST', path: 'upgrade-subscription', desc: 'Upgrade from Free to Pro', body: '{ "priceId": "price_..." }' },
  // Payment Methods
  { method: 'GET', path: 'check-payment-method', desc: 'Check if user has CC on file' },
  { method: 'POST', path: 'add-payment-method', desc: 'Get SetupIntent to add a card' },
  { method: 'POST', path: 'direct-purchase', desc: 'Charge card directly for a workflow', body: '{ "listingId": "..." }' },
  // Delete
  { method: 'POST', path: 'marketplace-delete', desc: 'Delete your listing (soft or hard)', body: '{ "listingId": "...", "hardDelete": false }' },
  // Admin
  { method: 'GET', path: 'marketplace-admin?action=queue', desc: 'List pending workflows (admin only)' },
  { method: 'POST', path: 'marketplace-admin', desc: 'Approve/reject/feedback (admin only)', body: '{ "action": "approve|reject|send_feedback", "listingId": "..." }' },
];

const ApiReference = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedExample, setCopiedExample] = useState(false);

  const curlExample = `curl -H "Authorization: Bearer ctxk_your_key_here" \\
  https://contextui.ai/.netlify/functions/marketplace?search=video`;

  const mcpConfig = `{
  "mcpServers": {
    "contextui-exchange": {
      "command": "node",
      "args": ["/path/to/mcp-server/index.js"],
      "env": {
        "CONTEXTUI_API_KEY": "ctxk_your_key_here",
        "CONTEXTUI_API_URL": "https://contextui.ai"
      }
    }
  }
}`;

  const handleCopyExample = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopiedExample(true);
    setTimeout(() => setCopiedExample(false), 2000);
  };

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-800/50 transition-colors"
      >
        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <BookOpen className="w-4 h-4 text-blue-400" />
        <span className="text-white font-medium">API Reference</span>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-5 border-t border-gray-700 pt-4">
          {/* Base URL */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Base URL</h4>
            <code className="block px-3 py-2 bg-gray-900 rounded text-sm text-blue-400 font-mono">
              https://contextui.ai/.netlify/functions/
            </code>
          </div>

          {/* Auth */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Authentication</h4>
            <p className="text-sm text-gray-400 mb-2">Include your API key in the Authorization header:</p>
            <code className="block px-3 py-2 bg-gray-900 rounded text-sm text-gray-300 font-mono">
              Authorization: Bearer ctxk_your_key_here
            </code>
          </div>

          {/* Endpoints table */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Endpoints</h4>
            <div className="overflow-x-auto rounded border border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Method</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Endpoint</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {API_ENDPOINTS.map((ep, i) => (
                    <tr key={i} className="hover:bg-gray-800/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-xs font-mono font-bold ${ep.method === 'GET' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {ep.method}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <code className="text-xs text-gray-300 font-mono">{ep.path}</code>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{ep.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick example */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Quick Example</h4>
            <div className="relative">
              <pre className="px-3 py-2 bg-gray-900 rounded text-xs text-gray-300 font-mono overflow-x-auto">
                {curlExample}
              </pre>
              <button
                onClick={() => handleCopyExample(curlExample)}
                className="absolute top-2 right-2 p-1 text-gray-500 hover:text-white transition-colors"
                title="Copy"
              >
                {copiedExample ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* MCP Server */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">MCP Server (for Claude Desktop / Clawdbot)</h4>
            <p className="text-sm text-gray-400 mb-2">
              Add this to your Claude Desktop config (<code className="text-gray-300">claude_desktop_config.json</code>):
            </p>
            <pre className="px-3 py-2 bg-gray-900 rounded text-xs text-gray-300 font-mono overflow-x-auto">
              {mcpConfig}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const ApiKeysTab = () => {
  const { user, getValidAccessToken } = useWorkOS();

  const [keys, setKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const fetchKeys = async () => {
    try {
      setIsLoading(true);
      const accessToken = await getValidAccessToken();
      const response = await fetch('/.netlify/functions/api-keys', {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch API keys');
      const data = await response.json();
      setKeys(data.keys || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.accessToken) fetchKeys();
  }, [user]);

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name) return;

    try {
      setError(null);
      const accessToken = await getValidAccessToken();
      const response = await fetch('/.netlify/functions/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ name })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create key');
      }

      const data = await response.json();
      setNewKeyValue(data.key);
      setNewKeyName('');
      fetchKeys();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      setRevokingId(keyId);
      setError(null);
      const accessToken = await getValidAccessToken();
      const response = await fetch(`/.netlify/functions/api-keys?id=${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) throw new Error('Failed to revoke key');
      fetchKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopy = async () => {
    if (!newKeyValue) return;
    await navigator.clipboard.writeText(newKeyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    }
    if (e.key === 'Escape') {
      setIsCreating(false);
      setNewKeyName('');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Key className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">API Keys</h3>
        </div>
        <p className="text-sm text-gray-400">
          Use API keys for programmatic access to the Exchange. Keys inherit your account permissions.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* New key reveal */}
      {newKeyValue && (
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            <p className="text-green-400 text-sm font-medium">Key created. Copy it now — it won't be shown again.</p>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-gray-900 rounded text-sm text-white font-mono break-all">
              {newKeyValue}
            </code>
            <button
              onClick={handleCopy}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <button
            onClick={() => setNewKeyValue(null)}
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create key */}
      {isCreating ? (
        <div className="p-4 bg-gray-800 rounded-lg space-y-3">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Key name, e.g. Clawdbot, CI Pipeline"
            maxLength={100}
            autoFocus
            className="w-full max-w-md px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              disabled={!newKeyName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              Create Key
            </button>
            <button
              onClick={() => { setIsCreating(false); setNewKeyName(''); }}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create New Key
        </button>
      )}

      {/* Keys list */}
      {keys.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No API keys yet.</p>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div
              key={k.id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                k.is_active
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-gray-800/50 border-gray-700/50 opacity-60'
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{k.name}</span>
                  {!k.is_active && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Revoked</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <code className="font-mono">{k.key_prefix}...</code>
                  <span>Created {new Date(k.created_at).toLocaleDateString()}</span>
                  {k.last_used_at && (
                    <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              {k.is_active && (
                <button
                  onClick={() => handleRevoke(k.id)}
                  disabled={revokingId === k.id}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Revoke key"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* API Reference */}
      <ApiReference />
    </div>
  );
};

export default ApiKeysTab;
